import React from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * MetricCard: Reusable enterprise LIMS dashboard metric card.
 * Strict Data Status Model:
 *  - LIVE: Valid, real operational data loaded from backend.
 *  - NO DATA: Module active & connected, but genuinely zero records for period.
 *  - MODULE NOT READY: Underlying backend/database capability not yet developed.
 *  - NOT CONFIGURED: Backend capability exists, but external gateway/setting is pending.
 *  - NO PERMISSION: Authenticated user lacks viewing authorization.
 *  - LOADING / ERROR: Network or runtime status.
 */
export const MetricCard = ({
  title,
  value,
  subtitle,
  icon,
  status = 'LIVE', // 'LIVE' | 'NO DATA' | 'MODULE NOT READY' | 'NOT CONFIGURED' | 'NO PERMISSION' | 'LOADING' | 'ERROR'
  route,
  routeParams = {},
  variant = 'default', // 'default' | 'danger' | 'warning' | 'success' | 'info' | 'purple'
  badge = null,
  onClick = null,
  trend = null
}) => {
  const navigate = useNavigate();

  const handleClick = () => {
    if (onClick) {
      onClick();
      return;
    }
    if (route && (status === 'LIVE' || status === 'NO DATA')) {
      const queryString = new URLSearchParams(routeParams).toString();
      navigate(queryString ? `${route}?${queryString}` : route);
    }
  };

  const isClickable = Boolean(onClick || (route && (status === 'LIVE' || status === 'NO DATA')));

  // Status Badge Rendering
  const renderStatusBadge = () => {
    switch (status) {
      case 'MODULE NOT READY':
        return <span className="lis-status-badge badge-not-ready">MODULE NOT READY</span>;
      case 'NOT CONFIGURED':
        return <span className="lis-status-badge badge-not-configured">NOT CONFIGURED</span>;
      case 'NO PERMISSION':
        return <span className="lis-status-badge badge-no-permission">RESTRICTED</span>;
      case 'NO DATA':
        return <span className="lis-status-badge badge-no-data">NO ACTIVITY</span>;
      case 'ERROR':
        return <span className="lis-status-badge badge-error">UNAVAILABLE</span>;
      case 'LOADING':
        return <span className="lis-status-badge badge-loading">SYNCING...</span>;
      default:
        if (badge) {
          return <span className="lis-status-badge badge-live">{badge}</span>;
        }
        return <span className="lis-status-badge badge-live">LIVE</span>;
    }
  };

  // Metric Value Display Logic (Zero Fake Data Guarantee)
  const renderValue = () => {
    if (status === 'LOADING') {
      return <div className="lis-metric-skeleton"></div>;
    }
    if (status === 'MODULE NOT READY') {
      return <span className="lis-metric-text-dim">Planned</span>;
    }
    if (status === 'NOT CONFIGURED') {
      return <span className="lis-metric-text-dim">Unconfigured</span>;
    }
    if (status === 'NO PERMISSION') {
      return <span className="lis-metric-text-dim">No Access</span>;
    }
    if (status === 'ERROR') {
      return <span className="lis-metric-text-dim error-text">Sync Failed</span>;
    }
    // Genuine numeric or text value (0 is rendered as 0)
    return <span className="lis-metric-number">{value !== null && value !== undefined ? value : 0}</span>;
  };

  return (
    <div
      className={`lis-metric-card lis-card-${variant} ${isClickable ? 'is-clickable' : ''}`}
      onClick={isClickable ? handleClick : undefined}
      role={isClickable ? 'button' : 'region'}
      tabIndex={isClickable ? 0 : undefined}
      title={isClickable ? `Click to inspect: ${title}` : title}
    >
      <div className="lis-metric-top-row">
        <div className="lis-metric-title-group">
          {icon && <span className="lis-metric-icon-inline">{icon}</span>}
          <span className="lis-metric-title">{title}</span>
        </div>
        {renderStatusBadge()}
      </div>

      <div className="lis-metric-val-row">
        {renderValue()}
      </div>

      <div className="lis-metric-bottom-row">
        {subtitle && <span className="lis-metric-subtitle">{subtitle}</span>}
        {isClickable && (
          <span className="lis-metric-drill-link">
            Details &rarr;
          </span>
        )}
      </div>

      {trend && (
        <div className="lis-metric-trend-row">
          <span>{trend}</span>
        </div>
      )}
    </div>
  );
};

export default MetricCard;
